import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map } from 'rxjs/operators'
import { GroupBy } from '../../app/common/model/hierarchy-menu.model'
import { GroupbyIconService } from '../../app/common/services/groupby-icon.service'
import { PpwLocalStorageService } from '../../app/common/services/ppwlocalstorage.service'
import { Utils } from '../../app/common/utils/utils'
import { UiConstants } from '../../app/ui/constants/ui-constants'
import { TimesheetContainer } from '../../app/ui/timesheets/timesheets.component'
import { allMonths, Month } from '../models/months.enum'
import { TimeCard, TimeCardStatus } from '../models/timecard.model'
import {
    calculateHours,
    fromJSON,
    getHttpParams,
    Timesheet,
    TimesheetGenCriteria,
    TimesheetPaginatedList,
    TimesheetSearchCriteria,
    TimesheetState
} from '../models/timesheet.model'
import { BaseService } from './base.service'

@Injectable({
    providedIn: 'root'
})
export class TimesheetService extends BaseService {
    private static sortTimesheets(items: Array<TimesheetContainer>): Array<TimesheetContainer> {
        return items.sort((a, b) => a.name.localeCompare(b.name))
    }

    private static sortTimesheetsByGroupBy(
        items: Array<TimesheetContainer>,
        groupBy: GroupBy
    ): Array<TimesheetContainer> {
        if (
            groupBy === 'client' ||
            groupBy === 'timesheetProjectClient' ||
            groupBy === 'month' ||
            groupBy === 'state'
        ) {
            items.forEach((t) => (t.name = t.employee.fullName))
        }
        if (groupBy === 'employee') {
            items.forEach((t) => (t.name = `${t.timesheetProject.clientName} - ${t.timesheetProject.name}`))
        }
        if (groupBy === 'tag') {
            items.forEach(
                (t) =>
                    (t.name = `${t.employee.fullName} - ${t.timesheetProject.clientName} - ${t.timesheetProject.name}`)
            )
        }

        return TimesheetService.sortTimesheets(items)
    }
    get uri(): string {
        return `${this.baseUri}/Timesheets`
    }

    constructor(
        private readonly ppwLocalStorageService: PpwLocalStorageService,
        readonly http: HttpClient,
        private readonly groupByIconService: GroupbyIconService
    ) {
        super(http)
    }

    async getNotInvoicedTimesheets(
        timesheetSearchCriteria: TimesheetSearchCriteria
    ): Promise<TimesheetPaginatedList | undefined> {
        const preferences = this.ppwLocalStorageService.getStoredData(
            this.ppwLocalStorageService.STORAGE_KEY_PROFILE_PREFERENCES
        )
        timesheetSearchCriteria.includeArchived = preferences && preferences.showArchivedData
        timesheetSearchCriteria.includeArchived = true // Marc S.: never hide logged hours in timecards nor timesheets
        timesheetSearchCriteria.toInvoice = true
        timesheetSearchCriteria.invoiced = false
        timesheetSearchCriteria.pageSize = 100000
        timesheetSearchCriteria.page = 1

        return this.$http
            .get<TimesheetPaginatedList>(this.uri, {
                params: timesheetSearchCriteria.httpParams(),
                observe: 'response'
            })
            .pipe(
                map((response) => TimesheetPaginatedList.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async approveTimesheet(timesheet: Timesheet): Promise<Timesheet> {
        const uri = `${this.uri}/approve`

        return this.$http
            .put<Timesheet>(uri, timesheet.timeCards, { observe: 'response' })
            .pipe(
                map((response) => fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async invoiceTimesheet(timesheet: Timesheet): Promise<Timesheet> {
        const uri = `${this.uri}/${timesheet.id}/invoice`

        return this.$http
            .get<Timesheet>(uri, { observe: 'response' })
            .pipe(
                map((response) => fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async setTimesheetToDoNotInvoice(timesheet: Timesheet): Promise<Timesheet> {
        const uri = `${this.uri}/${timesheet.id}/doNotInvoice`

        return this.$http
            .get<Timesheet>(uri, { observe: 'response' })
            .pipe(
                map((response) => fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async deleteTimesheet(timesheet: Timesheet, timecardResetStatus: TimeCardStatus): Promise<boolean | undefined> {
        const uri = `${this.uri}/${timesheet.id}`

        return this.$http
            .delete<Timesheet>(uri, { observe: 'response', params: { timecardResetStatus } })
            .pipe(
                map(() => true),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async getVirtualTimesheetsForMonth(genCriteria: TimesheetGenCriteria): Promise<Array<Timesheet> | undefined> {
        const copyGenCriteria: TimesheetGenCriteria = { ...genCriteria }
        const tagFilter = genCriteria.tag === UiConstants.MultiTag ? null : genCriteria.tag
        delete (copyGenCriteria as any).tag

        const uri = `${this.uri}/virtual`

        copyGenCriteria.includeArchived = true // Marc S.: never hide logged hours in timecards nor timesheets

        return this.$http
            .get<Array<Timesheet>>(uri, { params: getHttpParams(copyGenCriteria), observe: 'response' })
            .pipe(
                map((response) => {
                    const data = this.preCheckResponseData(response) as Array<Timesheet>
                    const list = new Array<Timesheet>()
                    const isViewingByTag = this.ppwLocalStorageService.getStoredData(
                        this.ppwLocalStorageService.STORAGE_KEY_VIEW_BY_TAG
                    )

                    data.forEach((jsonTimesheet: Timesheet) => {
                        const timesheet = fromJSON(jsonTimesheet)
                        if (tagFilter) {
                            timesheet.tag = tagFilter
                        }
                        if (isViewingByTag) {
                            // always break connection with real timesheets when viewing by tag
                            timesheet.id = 0
                        }
                        const filteredTimecards = new Array<TimeCard>()
                        timesheet.timeCards.forEach((timecard: TimeCard) => {
                            if (!tagFilter || timecard.tags.has(tagFilter)) {
                                filteredTimecards.push(timecard)
                            }
                        })
                        if (filteredTimecards.length > 0) {
                            timesheet.timeCards = filteredTimecards
                            calculateHours(timesheet)
                            list.push(timesheet)
                        }
                    })

                    return list
                }),
                catchError(async (err) => this.handleError(err))
            )
            .toPromise()
    }

    startGroupingTimesheets(items: Array<TimesheetContainer>, groupByArray: Array<GroupBy>): Array<TimesheetContainer> {
        const result: Array<TimesheetContainer> = this.groupTimesheetContainer(items, 'year')
        if (groupByArray.length > 1) {
            this.groupByAllChildren(result, groupByArray, groupByArray[0])
        }

        return result
    }

    readonly setTotalsForTimesheet = (timesheet: TimesheetContainer): void => {
        timesheet.hoursToCharge = Utils.secondsAsHours(timesheet.secondsToInvoice)
        timesheet.hoursNoCharge = Utils.secondsAsHours(timesheet.secondsNoInvoice)
        timesheet.hoursTotal = Utils.secondsAsHours(timesheet.secondsNoInvoice + timesheet.secondsToInvoice)
        timesheet.daysToCharge = Utils.secondsAsWorkingDay(timesheet.secondsToInvoice)
        timesheet.daysNoCharge = Utils.secondsAsWorkingDay(timesheet.secondsNoInvoice)
        timesheet.daysTotal = Utils.secondsAsWorkingDay(timesheet.secondsNoInvoice + timesheet.secondsToInvoice)
    }

    private groupByAllChildren(items: Array<TimesheetContainer>, groupBy: Array<GroupBy>, firstGroupBy: GroupBy): void {
        const isViewByTag = groupBy[0] === 'tag'
        items.forEach((timesheetContainer: TimesheetContainer) => {
            timesheetContainer.children = this.groupTimesheetContainer(
                timesheetContainer.children,
                groupBy[0],
                isViewByTag
            )
            if (groupBy.length > 1 && timesheetContainer.children.length > 0) {
                this.groupByAllChildren(timesheetContainer.children, groupBy.slice(1), firstGroupBy)
            }

            if (groupBy.length === 1 && timesheetContainer.children.length >= 1) {
                timesheetContainer.children.forEach((child) => {
                    child.children = TimesheetService.sortTimesheetsByGroupBy(child.children, firstGroupBy)
                })
            }
        })
    }

    private setTotals(timesheets: Array<TimesheetContainer>): void {
        timesheets.forEach((timesheet) => {
            timesheet.secondsNoInvoice = 0
            timesheet.secondsToInvoice = 0
            timesheet.children.forEach((child) => {
                timesheet.secondsToInvoice += child.secondsToInvoice
                timesheet.secondsNoInvoice += child.secondsNoInvoice
            })
            this.setTotalsForTimesheet(timesheet)
        })
    }

    private groupTimesheetContainer(
        items: Array<TimesheetContainer>,
        groupBy: GroupBy,
        isViewByTag?: boolean
    ): Array<TimesheetContainer> {
        let result: Array<TimesheetContainer> = []
        const colorByGroupBy = groupBy // We're going to map the groupBy to a color in the scss.
        const icon = this.groupByIconService.getIconByGroupBy(groupBy)
        if (groupBy === 'tag') {
            const tags: Set<string> = new Set()
            items.forEach((item) => {
                item.tags.forEach((tag) => {
                    tags.add(tag)
                })
            })

            tags.forEach((tag) => {
                let timesheetsWithTag = this.filterByTag(items, tag)
                if (isViewByTag) {
                    // Remove timecards from timesheet if they don't belong to this tag
                    // This is needed to get correct totals and show the correct data in the printable detail page
                    timesheetsWithTag = timesheetsWithTag.map((timesheet) => {
                        const copyTimesheet = { ...timesheet }
                        copyTimesheet.timeCards = copyTimesheet.timeCards.filter((t) => t.tags.has(tag))
                        copyTimesheet.secondsToInvoice = copyTimesheet.timeCards.reduce(
                            (acc, obj) => acc + obj.secondsToInvoice,
                            0
                        )
                        copyTimesheet.secondsNoInvoice = copyTimesheet.timeCards.reduce(
                            (acc, obj) => acc + obj.secondsNoInvoice,
                            0
                        )
                        copyTimesheet.tag = tag

                        return copyTimesheet
                    })
                }
                const row = {
                    name: tag,
                    colorByGroupBy,
                    icon,
                    children: timesheetsWithTag
                } as unknown as TimesheetContainer
                result.push(row)
            })
            result = TimesheetService.sortTimesheets(result)
        }
        if (groupBy === 'month') {
            // don't reverse the original array
            const copyAllMonths = [...allMonths]
            copyAllMonths.reverse().forEach((month) => {
                const children = this.filterByMonth(items, month)
                if (children.length > 0) {
                    const row = {
                        name: month.toString(),
                        colorByGroupBy,
                        icon,
                        children
                    } as unknown as TimesheetContainer
                    result.push(row)
                }
            })
        }

        if (groupBy === 'employee') {
            result = this.groupByEmployee(items, colorByGroupBy, icon)
            result = TimesheetService.sortTimesheets(result)
        }

        if (groupBy === 'timesheetProjectClient') {
            result = this.groupByTimesheetProjectClient(items, colorByGroupBy, icon)
            result = TimesheetService.sortTimesheets(result)
        }

        if (groupBy === 'client') {
            result = this.groupByClient(items, colorByGroupBy, icon)
            result = TimesheetService.sortTimesheets(result)
        }

        if (groupBy === 'timesheetProject') {
            result = this.groupByTimesheetProject(items, colorByGroupBy, icon)
            result = TimesheetService.sortTimesheets(result)
        }

        if (groupBy === 'state') {
            const toApprove: Array<TimesheetContainer> = this.filterByState(items, TimesheetState.TEMP)
            const toInvoice: Array<TimesheetContainer> = this.filterByState(items, TimesheetState.APPROVED)
            const noInvoice: Array<TimesheetContainer> = this.filterByState(items, TimesheetState.NO_INVOICE)
            const invoiced: Array<TimesheetContainer> = this.filterByState(items, TimesheetState.INVOICED)
            this.pushToResultIfNotEmpty('TO APPROVE', result, toApprove, colorByGroupBy, icon)
            this.pushToResultIfNotEmpty('APPROVED - TO INVOICE', result, toInvoice, colorByGroupBy, icon)
            this.pushToResultIfNotEmpty('APPROVED - DO NOT INVOICE', result, noInvoice, colorByGroupBy, icon)
            this.pushToResultIfNotEmpty('INVOICED', result, invoiced, colorByGroupBy, icon)
        }

        if (groupBy === 'year') {
            result = this.groupByYear(items, colorByGroupBy, icon)
        }

        this.setTotals(result)

        return result
    }

    private readonly pushToResultIfNotEmpty = (
        name: string,
        result: Array<TimesheetContainer>,
        children: Array<TimesheetContainer>,
        colorByGroupBy: string,
        icon: string
    ): void => {
        if (children.length > 0) {
            const row = { name, colorByGroupBy, icon, children } as unknown as TimesheetContainer
            result.push(row)
        }
    }

    private readonly filterByTag = (
        timesheetsToFilter: Array<TimesheetContainer>,
        tag: string
    ): Array<TimesheetContainer> => timesheetsToFilter.filter((t) => t.tags.has(tag))

    private readonly filterByMonth = (
        timesheetsToFilter: Array<TimesheetContainer>,
        month: Month
    ): Array<TimesheetContainer> =>
        // Note: We don't uppercase months, the backend does.
        timesheetsToFilter.filter((t) => t.month.toUpperCase() === month.toUpperCase())

    private readonly filterByState = (
        timesheetsToFilter: Array<TimesheetContainer>,
        state: TimesheetState
    ): Array<TimesheetContainer> => timesheetsToFilter.filter((t) => t.state === state)

    private readonly groupByEmployee = (
        items: Array<TimesheetContainer>,
        colorByGroupBy: string,
        icon: string
    ): Array<TimesheetContainer> =>
        items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children && a.employee
                    ? timesheet.children.findIndex((child) => child.employee.id === a.employee.id) >= 0
                    : false
            )

            if (id >= 0) {
                arr[id].children.push(a)
            } else {
                arr.push({
                    name: a.employee.fullName,
                    colorByGroupBy,
                    icon,
                    children: [a]
                } as unknown as TimesheetContainer)
            }

            return arr
        }, [] as Array<TimesheetContainer>)

    private readonly groupByTimesheetProjectClient = (
        items: Array<TimesheetContainer>,
        colorByGroupBy: string,
        icon: string
    ): Array<TimesheetContainer> =>
        items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children && a.timesheetProject
                    ? timesheet.children.findIndex((child) => child.timesheetProject.id === a.timesheetProject.id) >= 0
                    : false
            )

            if (id >= 0) {
                arr[id].children.push(a)
            } else {
                arr.push({
                    name: `${a.timesheetProject.clientName} - ${a.timesheetProject.name}`,
                    colorByGroupBy,
                    icon,
                    children: [a]
                } as unknown as TimesheetContainer)
            }

            return arr
        }, [] as Array<TimesheetContainer>)

    private readonly groupByTimesheetProject = (
        items: Array<TimesheetContainer>,
        colorByGroupBy: string,
        icon: string
    ): Array<TimesheetContainer> =>
        items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children && a.timesheetProject
                    ? timesheet.children.findIndex((child) => child.timesheetProject.id === a.timesheetProject.id) >= 0
                    : false
            )

            if (id >= 0) {
                arr[id].children.push(a)
            } else {
                arr.push({
                    name: `${a.timesheetProject.name}`,
                    colorByGroupBy,
                    icon,
                    children: [a]
                } as unknown as TimesheetContainer)
            }

            return arr
        }, [] as Array<TimesheetContainer>)

    private readonly groupByClient = (
        items: Array<TimesheetContainer>,
        colorByGroupBy: string,
        icon: string
    ): Array<TimesheetContainer> =>
        items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children && a.timesheetProject
                    ? timesheet.children.findIndex(
                          (child) => child.timesheetProject.clientId === a.timesheetProject.clientId
                      ) >= 0
                    : false
            )

            if (id >= 0) {
                arr[id].children.push(a)
            } else {
                arr.push({
                    name: `${a.timesheetProject.clientName}`,
                    colorByGroupBy,
                    icon,
                    children: [a]
                } as unknown as TimesheetContainer)
            }

            return arr
        }, [] as Array<TimesheetContainer>)

    private readonly groupByYear = (
        items: Array<TimesheetContainer>,
        colorByGroupBy: string,
        icon: string
    ): Array<TimesheetContainer> =>
        items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children && a.year
                    ? timesheet.children.findIndex((child) => child.year === a.year) >= 0
                    : false
            )

            if (id >= 0) {
                arr[id].children.push(a)
            } else {
                arr.push({ name: `${a.year}`, colorByGroupBy, icon, children: [a] } as unknown as TimesheetContainer)
            }

            return arr
        }, [] as Array<TimesheetContainer>)
}
